import { useContext, useMemo } from 'react'
import { Dices, Info } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTeam } from 'app/frontend/base/config/context/team'
import {
  useCharacterWishlist,
  usePageInfo,
} from 'app/frontend/base/config/queries'
import { updateBonusRollTarget } from 'app/frontend/base/api/character'
import { displayErrorToast } from 'app/frontend/base/helpers'
import { useTranslation } from 'react-i18next'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Badge } from '@/components/ui/badge'
import ItemIcon from 'app/frontend/base/components/ItemIcon'
import { LootContext } from '../context'
import {
  EncounterUpgradeCounts,
  bonusRollWishDifficultyIndex,
  encounterUpgradeCountsBySource,
} from '../helpers'

type Props = {
  characterId: number
  disabled?: boolean
}

const countClass = (count: number, activeClass: string) =>
  count > 0 ? activeClass : 'text-muted-foreground'

const UPGRADE_CATEGORIES: {
  key: keyof EncounterUpgradeCounts
  label: string
  valueColor: string
}[] = [
  { key: 'bis', label: 'BiS', valueColor: 'text-success-foreground' },
  {
    key: 'otherUpgrades',
    label: 'Other upgrades',
    valueColor: 'text-warning-foreground',
  },
  {
    key: 'notUpgrades',
    label: 'Not an upgrade',
    valueColor: 'text-muted-foreground',
  },
]

const EncounterUpgradeBadge = ({
  counts,
  wishDifficultyIndex,
}: {
  counts: EncounterUpgradeCounts
  wishDifficultyIndex: number
}) => {
  const {
    data: { gameVersion },
  } = usePageInfo()
  const { configuration, valueDisplay } = useContext(LootContext)
  const sections = UPGRADE_CATEGORIES.filter(
    (category) => counts[category.key].length > 0,
  )

  const formatWishValue = (wish?: wowaudit.Wish) => {
    if (valueDisplay === 'percentage') {
      return `${(wish?.percentage ?? 0).toLocaleString('en-US', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      })}%`
    }

    return (wish?.absolute ?? 0).toLocaleString('en-US', {
      maximumFractionDigits: 0,
    })
  }

  const itemHref = (item: wowaudit.WishlistItem, wish?: wowaudit.Wish) => {
    const baseBonusIds =
      item.bonus_ids_by_configuration[
        `${configuration.id}|${wishDifficultyIndex}`
      ]
    const itemLevel =
      item.item_level_by_configuration[
        `${configuration.id}|${wishDifficultyIndex}`
      ]
    const hrefBonusIds = wish?.bonus_ids ?? baseBonusIds?.join(':')

    return `https://wowhead.com/${
      gameVersion === 'classic_progression' ? 'mop-classic/' : ''
    }item=${item.id}${
      itemLevel ? `?ilvl=${itemLevel}` : ''
    }&bonus=${hrefBonusIds}`
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className='inline-flex cursor-default'>
          <Badge
            variant='outline'
            className='px-1.5 py-0 font-normal text-sm whitespace-nowrap cursor-default'
          >
            <span className='tabular-nums flex items-center gap-0.5'>
              <span
                className={countClass(
                  counts.bis.length,
                  'text-success-foreground',
                )}
              >
                {counts.bis.length}
              </span>
              <span className='text-muted-foreground'>/</span>
              <span
                className={countClass(
                  counts.otherUpgrades.length,
                  'text-warning-foreground',
                )}
              >
                {counts.otherUpgrades.length}
              </span>
              <span className='text-muted-foreground'>/</span>
              <span
                className={countClass(
                  counts.notUpgrades.length,
                  'text-destructive-foreground',
                )}
              >
                {counts.notUpgrades.length}
              </span>
            </span>
          </Badge>
        </span>
      </TooltipTrigger>
      <TooltipContent side='bottom' className='p-0 min-w-56 max-w-80'>
        {sections.map((section, index) => (
          <div key={section.key} className={index > 0 ? 'border-t' : undefined}>
            <div className='px-2.5 pt-1.5 pb-1 text-xs font-medium'>
              {section.label}
            </div>
            <ul className='px-2.5 pb-1.5 flex flex-col gap-1'>
              {counts[section.key].map(({ item, wish }) => (
                <li key={item.id}>
                  <a
                    href={itemHref(item, wish)}
                    target='_blank'
                    rel='noreferrer'
                    className='flex items-center gap-3 min-w-0'
                  >
                    <ItemIcon
                      id={item.id}
                      name={item.icon}
                      className='w-4 h-4 rounded-sm shrink-0'
                    />
                    <span
                      className={`text-sm truncate min-w-0 grow text-quality-${item.quality}`}
                    >
                      {item.name}
                    </span>
                    <span
                      className={`text-sm tabular-nums shrink-0 text-right ${section.valueColor}`}
                    >
                      {section.key === 'notUpgrades'
                        ? '-'
                        : formatWishValue(wish)}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </TooltipContent>
    </Tooltip>
  )
}

const BonusRollTarget = ({ characterId, disabled }: Props) => {
  const { team } = useTeam()
  const { selectedSeason, difficulty, configuration } = useContext(LootContext)
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { data: wishlist } = useCharacterWishlist(
    team.id,
    characterId,
    selectedSeason.id,
  )

  const teamQueryKey = ['team', { path: team.path }]

  const encounters = selectedSeason.instances.flatMap(
    (instance) => instance.encounters,
  )

  const member = team.members?.find(
    (teamMember) => teamMember.characterReference.id === characterId,
  )

  const selectedIds = member?.characterReference.bonusRollEncounterIds ?? []

  const upgradeCountsByEncounter = useMemo(() => {
    if (!wishlist) return null

    const specId =
      wishlist.specs.find((spec) => spec.default)?.id ??
      member?.characterReference.currentSpec?.id ??
      member?.characterReference.currentSpecId ??
      wishlist.specs[0]?.id
    if (!specId) return null

    const wishDifficultyIndex = bonusRollWishDifficultyIndex(
      wishlist.items,
      specId,
      difficulty,
      configuration.id,
    )
    if (wishDifficultyIndex == null) return null

    const counts = encounterUpgradeCountsBySource(
      wishlist.items,
      specId,
      wishDifficultyIndex,
      configuration.id,
    )
    if (!counts) return null

    return { counts, wishDifficultyIndex }
  }, [wishlist, member, difficulty, configuration.id])

  const mutation = useMutation({
    mutationFn: (nextIds: number[]) =>
      updateBonusRollTarget(team.id, characterId, nextIds),
    onMutate: async (nextIds) => {
      await queryClient.cancelQueries({ queryKey: teamQueryKey })
      const previousTeam = queryClient.getQueryData<wowaudit.Team>(teamQueryKey)

      queryClient.setQueryData<wowaudit.Team>(teamQueryKey, (old) =>
        old
          ? {
              ...old,
              members: old.members?.map((member) =>
                member.characterReference.id === characterId
                  ? {
                      ...member,
                      characterReference: {
                        ...member.characterReference,
                        bonusRollEncounterIds: nextIds,
                      },
                    }
                  : member,
              ),
            }
          : old,
      )

      return { previousTeam }
    },
    onError: (error, _nextIds, context) => {
      if (context?.previousTeam) {
        queryClient.setQueryData(teamQueryKey, context.previousTeam)
      }
      displayErrorToast(error, t)
    },
    onSuccess: (newTeam) => {
      queryClient.setQueryData(teamQueryKey, newTeam)
    },
  })

  const toggleEncounter = (encounterId: number) => {
    if (disabled || mutation.isPending) return

    mutation.mutate(
      selectedIds.includes(encounterId)
        ? selectedIds.filter((id) => id !== encounterId)
        : [...selectedIds, encounterId],
    )
  }

  if (!selectedSeason.bonus_rolls_available || encounters.length === 0)
    return null

  return (
    <div>
      <h4 className='flex items-center gap-2 mb-1'>
        <Dices size={20} className='text-info-foreground' />
        Bonus roll targets
        {upgradeCountsByEncounter ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className='inline-flex text-muted-foreground hover:text-foreground'>
                <Info size={14} />
              </span>
            </TooltipTrigger>
            <TooltipContent className='flex flex-col gap-1'>
              <div>
                <span className='text-success-foreground'>Best in slot</span>
                {' / '}
                <span className='text-warning-foreground'>other upgrades</span>
                {' / '}
                <span className='text-destructive-foreground'>
                  not an upgrade
                </span>
              </div>
              <div className='text-center'>
                Based on your currently uploaded wishes
              </div>
            </TooltipContent>
          </Tooltip>
        ) : null}
      </h4>
      <div className='grid gap-2 grid-cols-[repeat(auto-fit,minmax(5rem,1fr))]'>
        {encounters.map((encounter) => {
          const active = selectedIds.includes(encounter.id)
          const counts = upgradeCountsByEncounter?.counts[encounter.name]
          const totalItems = counts
            ? counts.bis.length +
              counts.otherUpgrades.length +
              counts.notUpgrades.length
            : 0

          return (
            <button
              key={encounter.id}
              type='button'
              disabled={disabled || mutation.isPending}
              onClick={() => toggleEncounter(encounter.id)}
              className={`hover:bg-accent min-w-0 flex flex-col items-center gap-1 px-2 pt-2 pb-1.5 rounded-md border transition-colors disabled:cursor-not-allowed group ${
                active ? 'border-success-foreground bg-accent' : 'bg-card'
              }`}
            >
              <img
                src={encounter.thumbnailUrl}
                className={`w-10 h-10 rounded-full object-cover shrink-0 transition-all ${
                  active ? '' : 'not-group-hover:grayscale-100'
                }`}
                alt={encounter.name}
              />
              <span
                className={`text-sm text-center truncate w-full font-normal ${
                  active ? 'text-success-foreground' : ''
                }`}
              >
                {encounter.name}
              </span>
              {counts && totalItems > 0 ? (
                <EncounterUpgradeBadge
                  counts={counts}
                  wishDifficultyIndex={
                    upgradeCountsByEncounter.wishDifficultyIndex
                  }
                />
              ) : null}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default BonusRollTarget
